/* ============================================================
 * Vangart — moteur d'animations au défilement.
 * Progressive enhancement, idempotent, compatible éditeur de thème.
 * - Reveal : [data-vg-reveal] -> ajoute .vg-in à l'entrée dans le viewport
 * - Compteurs : [data-vg-count="120"] (option data-vg-count-suffix)
 * - Parallaxe : [data-vg-parallax="0.15"] -> pilote --vg-parallax-y
 * Respecte prefers-reduced-motion (aucune animation, contenu visible).
 * ============================================================ */
(() => {
  const root = document.documentElement;
  const reduceMq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMq && reduceMq.matches) return; // tout reste visible, pas d'animation

  root.classList.add('vg-reveal-enabled');

  const supportsIO = 'IntersectionObserver' in window;

  /* --- Reveal ---------------------------------------------- */
  const revealObserver = supportsIO
    ? new IntersectionObserver(
        (entries, obs) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('vg-in');
              obs.unobserve(entry.target);
            }
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      )
    : null;

  const observeReveals = (scope) => {
    const els = (scope || document).querySelectorAll('[data-vg-reveal]:not(.vg-observed)');
    els.forEach((el) => {
      el.classList.add('vg-observed');
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('vg-in');
    });
  };

  /* Filet de sécurité : l'IntersectionObserver est parfois pris en défaut
     (éditeur de thème, scroll programmatique, layout tardif), laissant des
     visuels « clip » figés masqués. On révèle alors tout élément déjà visible
     à l'écran qui n'a pas encore reçu .vg-in ; le reste continue d'être animé
     au défilement. */
  const revealInView = (scope) => {
    const vh = window.innerHeight || document.documentElement.clientHeight;
    (scope || document).querySelectorAll('[data-vg-reveal]:not(.vg-in)').forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < vh && rect.bottom > 0) el.classList.add('vg-in');
    });
  };

  /* --- Compteurs ------------------------------------------- */
  const runCount = (el) => {
    if (el.dataset.vgCountDone) return;
    el.dataset.vgCountDone = '1';
    const target = parseFloat(el.getAttribute('data-vg-count')) || 0;
    const suffix = el.getAttribute('data-vg-count-suffix') || '';
    const prefix = el.getAttribute('data-vg-count-prefix') || '';
    const duration = 1700;
    let startTime = null;
    const step = (now) => {
      if (startTime === null) startTime = now;
      const p = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const val = Math.round(target * eased);
      el.textContent = prefix + val.toLocaleString('fr-FR') + suffix;
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  const countObserver = supportsIO
    ? new IntersectionObserver(
        (entries, obs) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              runCount(entry.target);
              obs.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.5 }
      )
    : null;

  const observeCounts = (scope) => {
    const els = (scope || document).querySelectorAll('[data-vg-count]:not(.vg-count-bound)');
    els.forEach((el) => {
      el.classList.add('vg-count-bound');
      if (countObserver) countObserver.observe(el);
      else runCount(el);
    });
  };

  /* --- Parallaxe ------------------------------------------- */
  let parallaxEls = [];
  const collectParallax = () => {
    parallaxEls = Array.from(document.querySelectorAll('[data-vg-parallax]'));
  };

  let ticking = false;
  const updateParallax = () => {
    ticking = false;
    if (!parallaxEls.length) return;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    for (const el of parallaxEls) {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > vh + 200) continue;
      const speed = parseFloat(el.getAttribute('data-vg-parallax')) || 0.15;
      const fromCenter = rect.top + rect.height / 2 - vh / 2;
      el.style.setProperty('--vg-parallax-y', (fromCenter * -speed).toFixed(1) + 'px');
    }
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updateParallax);
  };

  /* --- Init ------------------------------------------------ */
  const init = (scope) => {
    observeReveals(scope);
    observeCounts(scope);
    collectParallax();
    onScroll();
    // Si l'observer n'a rien déclenché peu après l'init, on révèle ce qui est
    // déjà à l'écran (cas typique de l'éditeur de thème).
    window.setTimeout(() => revealInView(scope), 600);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  // Éditeur de thème : re-scanner les sections (re)chargées
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
